import { supabase } from "../lib/supabase";

/**
 * DASHBOARD STATS
 */
export const getAdminStats = async () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayIso = today.toISOString();

  const [ordersTodayRes, ordersRes, productsRes, usersRes] = await Promise.all([
    supabase
      .from("orders")
      .select("id", { count: "exact", head: true })
      .gte("created_at", todayIso),

    supabase
      .from("orders")
      .select("total_amount"),

    supabase
      .from("products")
      .select("id", { count: "exact", head: true })
      .eq("available", true),

    supabase
      .from("profiles")
      .select("id", { count: "exact", head: true }),
  ]);

  if (ordersTodayRes.error) throw ordersTodayRes.error;
  if (ordersRes.error) throw ordersRes.error;
  if (productsRes.error) throw productsRes.error;
  if (usersRes.error) throw usersRes.error;

  const revenue = (ordersRes.data ?? []).reduce((sum, order) => {
    return sum + (Number(order.total_amount) || 0);
  }, 0);

  return {
    ordersToday: ordersTodayRes.count ?? 0,
    revenue,
    activeProducts: productsRes.count ?? 0,
    usersCount: usersRes.count ?? 0,
  };
};

/**
 * USERS
 */
export const getAdminUsers = async () => {
  const { data, error } = await supabase
    .from("profiles")
    .select(`
      id,
      email,
      first_name,
      last_name,
      phone,
      role,
      is_active,
      marketing_consent,
      created_at,
      updated_at
    `)
    .order("created_at", { ascending: false });

  if (error) {
    console.error("Erreur récupération utilisateurs:", error);
    throw error;
  }

  return data ?? [];
};

export const updateUserRole = async (userId, role) => {
  const { data, error } = await supabase
    .from("profiles")
    .update({ role, updated_at: new Date().toISOString() })
    .eq("id", userId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

/**
 * PRODUCTS
 * ✅ Jointure sur categories via category_id
 */
export const getAdminProducts = async () => {
  const { data, error } = await supabase
    .from("products")
    .select(`
      id,
      name,
      slug,
      short_description,
      description,
      price,
      stock,
      available,
      featured,
      has_variants,
      image_url,
      tags,
      category_id,
      categories (
        id,
        name,
        slug
      ),
      created_at,
      updated_at
    `)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return data ?? [];
};

export const updateProductById = async (productId, updates) => {
  // Nettoyer les champs non-colonnes avant l'update
  const { categories, category, ...cleanUpdates } = updates;

  const { data, error } = await supabase
    .from("products")
    .update({
      ...cleanUpdates,
      updated_at: new Date().toISOString(),
    })
    .eq("id", productId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const deleteProductById = async (productId) => {
  const { error } = await supabase
    .from("products")
    .delete()
    .eq("id", productId);

  if (error) throw error;
  return true;
};

/**
 * PRODUCT IMAGE UPLOAD
 */
export const uploadProductImage = async (file, productId) => {
  if (!file) {
    throw new Error("Aucun fichier sélectionné.");
  }

  if (!productId) {
    throw new Error("L'identifiant du produit est requis.");
  }

  const fileExtension =
    file.name.split(".").pop()?.toLowerCase() || "jpg";

  const fileName = `${crypto.randomUUID()}.${fileExtension}`;

  const filePath = `${productId}/${fileName}`;

  const { error: uploadError } = await supabase.storage
    .from("product-images")
    .upload(filePath, file, {
      cacheControl: "3600",
      upsert: false,
      contentType: file.type,
    });

  if (uploadError) {
    throw uploadError;
  }

  const {
    data: { publicUrl },
  } = supabase.storage
    .from("product-images")
    .getPublicUrl(filePath);

  return {
    path: filePath,
    url: publicUrl,
  };
};

export const uploadProductImages = async (
  files,
  productId,
) => {
  if (!files?.length) {
    return [];
  }

  if (!productId) {
    throw new Error("L'identifiant du produit est requis.");
  }

  const uploadedImages = [];

  for (let index = 0; index < files.length; index++) {
    const file = files[index];

    const uploaded = await uploadProductImage(
      file,
      productId,
    );

    uploadedImages.push({
      url: uploaded.url,
      path: uploaded.path,
      sort_order: index,
    });
  }

  return uploadedImages;
};

export const createProductImages = async (
  productId,
  images,
) => {
  if (!productId || !images?.length) {
    return [];
  }

  const payload = images.map((image, index) => ({
    product_id: productId,
    image_url: image.url,
    sort_order:
      image.sort_order ?? index,
  }));

  const {
    data,
    error,
  } = await supabase
    .from("product_images")
    .insert(payload)
    .select();

  if (error) {
    throw error;
  }

  return data;
};

/**
 * PRODUCT EDITING
 * Variantes + images
 */

/**
 * Récupère un produit complet avec :
 * - ses informations
 * - ses images générales
 * - ses variantes
 * - les images de chaque variante
 */
export const getAdminProductById = async (productId) => {
  if (!productId) {
    throw new Error("L'identifiant du produit est requis.");
  }

  const { data: product, error: productError } = await supabase
    .from("products")
    .select(`
      id,
      name,
      slug,
      short_description,
      description,
      price,
      stock,
      available,
      featured,
      has_variants,
      image_url,
      tags,
      category_id,
      created_at,
      updated_at
    `)
    .eq("id", productId)
    .single();

  if (productError) throw productError;

  const { data: variants, error: variantsError } = await supabase
    .from("product_variants")
    .select(`
      id,
      product_id,
      name,
      slug,
      description,
      price,
      image_url,
      available,
      stock,
      sort_order,
      created_at,
      updated_at
    `)
    .eq("product_id", productId)
    .order("sort_order", { ascending: true });

  if (variantsError) throw variantsError;

  const { data: images, error: imagesError } = await supabase
    .from("product_images")
    .select(`
      id,
      product_id,
      variant_id,
      image_url,
      sort_order,
      created_at
    `)
    .eq("product_id", productId)
    .order("sort_order", { ascending: true });

  if (imagesError) throw imagesError;

  const allImages = images ?? [];

  const productImages = allImages.filter(
    (image) => !image.variant_id,
  );

  const productVariants = (variants ?? []).map((variant) => ({
    ...variant,
    images: allImages.filter(
      (image) => image.variant_id === variant.id,
    ),
  }));

  return {
    ...product,
    images: productImages,
    variants: productVariants,
  };
};

/**
 * Crée une variante sur un produit existant.
 */
export const createProductVariant = async (
  productId,
  variant,
) => {
  if (!productId) {
    throw new Error("L'identifiant du produit est requis.");
  }

  const name = variant.name?.trim();

  if (!name) {
    throw new Error("Le nom de la variante est requis.");
  }

  const slug =
    variant.slug?.trim() ||
    name
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

  const { data, error } = await supabase
    .from("product_variants")
    .insert([
      {
        product_id: productId,
        name,
        slug,
        description: variant.description?.trim() || null,
        price: Number(variant.price || 0),
        stock: Number(variant.stock ?? 0),
        available: variant.available ?? true,
        sort_order: Number(variant.sort_order ?? 0),
        image_url: null,
      },
    ])
    .select()
    .single();

  if (error) throw error;

  return data;
};

/**
 * Modifie une variante existante.
 */
export const updateProductVariant = async (
  variantId,
  updates,
) => {
  if (!variantId) {
    throw new Error("L'identifiant de la variante est requis.");
  }

  const cleanUpdates = {
    ...updates,
    updated_at: new Date().toISOString(),
  };

  delete cleanUpdates.id;
  delete cleanUpdates.product_id;
  delete cleanUpdates.images;
  delete cleanUpdates.created_at;

  const { data, error } = await supabase
    .from("product_variants")
    .update(cleanUpdates)
    .eq("id", variantId)
    .select()
    .single();

  if (error) throw error;

  return data;
};

/**
 * Supprime une variante.
 *
 * Grâce au ON DELETE CASCADE :
 * les lignes product_images liées à cette variante
 * seront supprimées automatiquement.
 */
export const deleteProductVariant = async (variantId) => {
  if (!variantId) {
    throw new Error("L'identifiant de la variante est requis.");
  }

  const { error } = await supabase
    .from("product_variants")
    .delete()
    .eq("id", variantId);

  if (error) throw error;

  return true;
};

/**
 * Ajoute plusieurs images générales à un produit existant.
 */
export const addProductImages = async (
  productId,
  files,
) => {
  if (!files?.length) {
    return [];
  }

  const uploadedImages = await uploadProductImages(
    files,
    productId,
  );

  const { data: existingImages, error: existingError } =
    await supabase
      .from("product_images")
      .select("id, sort_order")
      .eq("product_id", productId)
      .is("variant_id", null)
      .order("sort_order", { ascending: false });

  if (existingError) throw existingError;

  const nextSortOrder =
    existingImages?.length > 0
      ? Number(existingImages[0].sort_order) + 1
      : 0;

  const payload = uploadedImages.map((image, index) => ({
    product_id: productId,
    variant_id: null,
    image_url: image.url,
    sort_order: nextSortOrder + index,
  }));

  const { data, error } = await supabase
    .from("product_images")
    .insert(payload)
    .select();

  if (error) throw error;

  // Si le produit n'avait pas encore d'image principale,
  // la première image ajoutée devient son image principale.
  const { data: product, error: productError } = await supabase
    .from("products")
    .select("image_url")
    .eq("id", productId)
    .single();

  if (productError) throw productError;

  if (!product.image_url && uploadedImages.length > 0) {
    await updateProductById(productId, {
      image_url: uploadedImages[0].url,
    });
  }

  return data ?? [];
};

/**
 * Ajoute plusieurs images à une variante existante.
 */
export const addVariantImages = async (
  productId,
  variantId,
  files,
) => {
  if (!files?.length) {
    return [];
  }

  if (!productId || !variantId) {
    throw new Error(
      "Le produit et la variante sont requis.",
    );
  }

  const uploadedImages = await uploadProductImages(
    files,
    productId,
  );

  const { data: existingImages, error: existingError } =
    await supabase
      .from("product_images")
      .select("id, sort_order")
      .eq("product_id", productId)
      .eq("variant_id", variantId)
      .order("sort_order", { ascending: false });

  if (existingError) throw existingError;

  const nextSortOrder =
    existingImages?.length > 0
      ? Number(existingImages[0].sort_order) + 1
      : 0;

  const payload = uploadedImages.map((image, index) => ({
    product_id: productId,
    variant_id: variantId,
    image_url: image.url,
    sort_order: nextSortOrder + index,
  }));

  const { data, error } = await supabase
    .from("product_images")
    .insert(payload)
    .select();

  if (error) throw error;

  // Si la variante n'avait pas encore d'image principale,
  // la première ajoutée devient son image principale.
  const { data: variant, error: variantError } = await supabase
    .from("product_variants")
    .select("image_url")
    .eq("id", variantId)
    .single();

  if (variantError) throw variantError;

  if (!variant.image_url && uploadedImages.length > 0) {
    await updateProductVariant(variantId, {
      image_url: uploadedImages[0].url,
    });
  }

  return data ?? [];
};

/**
 * Supprime une image de product_images.
 *
 * On ne supprime volontairement pas le fichier du Storage.
 */
export const deleteProductImage = async (imageId) => {
  if (!imageId) {
    throw new Error("L'identifiant de l'image est requis.");
  }

  const { error } = await supabase
    .from("product_images")
    .delete()
    .eq("id", imageId);

  if (error) throw error;

  return true;
};

/**
 * ORDERS
 */
export const getAdminOrders = async () => {
  const { data, error } = await supabase
    .from("orders")
    .select(`
      id,
      user_id,
      customer_first_name,
      customer_last_name,
      customer_email,
      customer_phone,
      status,
      payment_status,
      total_amount,
      stripe_session_id,
      pickup_type,
      notes,
      admin_notes,
      created_at,
      updated_at,
      order_items (
        id,
        product_id,
        product_name,
        unit_price,
        quantity,
        total_price,
        created_at
      )
    `)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return data ?? [];
};

export const updateOrderStatus = async (orderId, status) => {
  const { data, error } = await supabase
    .from("orders")
    .update({ status, updated_at: new Date().toISOString() })
    .eq("id", orderId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

/**
 * CATEGORIES
 */
export const getCategories = async () => {
  const { data, error } = await supabase
    .from("categories")
    .select("id, name, slug")
    .order("sort_order", { ascending: true });

  if (error) throw error;
  return data ?? [];
};

export const getNewsletterSubscribers = async () => {
  const { data, error } = await supabase
    .from('newsletter_subscribers')
    .select('id, email, first_name, subscribed, created_at')
    .order('created_at', { ascending: false })

  if (error) throw error
  return data ?? []
}

export const sendNewsletter = async ({ subject, html, preview_text }) => {
  const { data, error } = await supabase.functions.invoke('send-newsletter', {
    body: { subject, html, preview_text },
  })

  if (error) throw error
  return data
}

export const createProduct = async (payload) => {
  const {
    category,
    images = [],
    variants = [],
    ...productPayload
  } = payload;

  const cleanProductPayload = {
    ...productPayload,
    name: productPayload.name?.trim(),
    slug: productPayload.slug?.trim(),
    category_id:
      payload.category_id || category || null,
    image_url: null,
    has_variants:
      Boolean(payload.has_variants),
  };

  // 1. Création du produit
  const {
    data: product,
    error: productError,
  } = await supabase
    .from("products")
    .insert([cleanProductPayload])
    .select()
    .single();

  if (productError) {
    throw productError;
  }

  try {
    // 2. Upload des images principales du produit
    if (images.length > 0) {
      const uploadedImages = await uploadProductImages(
        images,
        product.id,
      );

      await createProductImages(
        product.id,
        uploadedImages,
      );

      // Première image = image principale du produit
      if (uploadedImages.length > 0) {
        const { error: updateImageError } = await supabase
          .from("products")
          .update({
            image_url: uploadedImages[0].url,
            updated_at: new Date().toISOString(),
          })
          .eq("id", product.id);

        if (updateImageError) {
          throw updateImageError;
        }
      }
    }

    // 3. Création des variantes
    if (
      cleanProductPayload.has_variants &&
      variants.length > 0
    ) {
      const variantsPayload = variants.map(
        (variant, index) => ({
          product_id: product.id,

          name: variant.name.trim(),

          slug:
            variant.slug?.trim() ||
            variant.name
              .toLowerCase()
              .normalize("NFD")
              .replace(/[\u0300-\u036f]/g, "")
              .replace(/[^a-z0-9]+/g, "-")
              .replace(/^-+|-+$/g, ""),

          description:
            variant.description?.trim() || null,

          price: Number(variant.price),

          image_url: null,

          available:
            variant.available ?? true,

          stock: Number(
            variant.stock ?? 100,
          ),

          sort_order: Number(
            variant.sort_order ?? index,
          ),
        }),
      );

      const {
        data: createdVariants,
        error: variantsError,
      } = await supabase
        .from("product_variants")
        .insert(variantsPayload)
        .select();

      if (variantsError) {
        throw variantsError;
      }

      // 4. Upload des images de chaque variante
      for (
        let i = 0;
        i < variants.length;
        i++
      ) {
        const variantImages =
          variants[i].images || [];

        if (!variantImages.length) {
          continue;
        }

        const uploadedImages =
          await uploadProductImages(
            variantImages,
            product.id,
          );

        const imagePayload =
          uploadedImages.map(
            (image, imageIndex) => ({
              product_id: product.id,
              variant_id:
                createdVariants[i].id,
              image_url: image.url,
              sort_order: imageIndex,
            }),
          );

        const {
          error: imageError,
        } = await supabase
          .from("product_images")
          .insert(imagePayload);

        if (imageError) {
          throw imageError;
        }

        // Première image = image principale de la variante
        if (uploadedImages.length > 0) {
          const { error: variantImageError } = await supabase
            .from("product_variants")
            .update({
              image_url: uploadedImages[0].url,
            })
            .eq("id", createdVariants[i].id);

          if (variantImageError) {
            throw variantImageError;
          }
        }
      }
    }
  } catch (error) {
    // Nettoyage de la base si une étape échoue.
    await supabase
      .from("products")
      .delete()
      .eq("id", product.id);

    throw error;
  }

  // L'identifiant est retourné au formulaire
  return product.id;
};

export const updateUserStatus = async (userId, isActive) => {
  const { data, error } = await supabase
    .from("profiles")
    .update({
      is_active: isActive,
      updated_at: new Date().toISOString(),
    })
    .eq("id", userId)
    .select()
    .single();

  if (error) {
    throw error;
  }

  return data;
};

export const createAdminUser = async (payload) => {
  const { data, error } =
    await supabase.functions.invoke(
      "create-admin-user",
      {
        body: payload,
      },
    );

  if (error) {
    throw error;
  }

  if (!data?.success) {
    throw new Error(
      data?.error ||
      "Impossible de créer l'utilisateur.",
    );
  }

  return data;
};